import { useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { cn } from '@/lib/utils';
import {
	RANGE_PRESETS,
	RANGE_PRESET_LABELS,
	describeRange,
	isCustomRange,
	type ActionLogDateRange
} from '../action-log-constants';

/**
 * The window the log is read over.
 *
 * Always bounded, never "all time": the table is append-only and only grows, so an unbounded read
 * is a scan that gets slower every day the agent runs. The server defaults to seven days for the
 * same reason, and this control starts on the same seven so the two never quietly disagree.
 */

interface ActionLogDateRangeFilterProps {
	value: ActionLogDateRange;
	onChange: (next: ActionLogDateRange) => void;
}

export const ActionLogDateRangeFilter = ({ value, onChange }: ActionLogDateRangeFilterProps) => {
	const [open, setOpen] = useState(false);
	const isNarrowed = isCustomRange(value);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant='outline'
					// Figma 4768:15320. 28px tall, 12/10 padding, a gray-500 hairline and no shadow,
					// with the label AND both icons in the headings colour — the design greys nothing
					// here. The 8px gap is to the chevron; the calendar sits 6px off its label, so the
					// icon reads as part of the label rather than as a second control.
					className={cn(
						'h-7 gap-2 rounded-full border-gray-500 pl-3 pr-2.5 text-[13px] leading-[18px] font-normal text-ink-strong shadow-none',
						isNarrowed && 'border-ink-strong font-medium'
					)}
					aria-label='Date range filter'
				>
					<span className='flex items-center gap-1.5'>
						<CalendarDays className='size-3.5' />
						{describeRange(value)}
					</span>
					<ChevronDown className='size-3.5' />
				</Button>
			</PopoverTrigger>

			<PopoverContent align='start' className='w-[300px] rounded-2xl p-3'>
				<p className='px-2 pb-1 text-xs text-muted-foreground'>Date range</p>

				{RANGE_PRESETS.map((preset) => (
					<button
						key={preset}
						type='button'
						className={cn(
							'flex w-full cursor-pointer items-center rounded-lg px-2 py-2 text-left text-[13px] text-foreground hover:bg-muted',
							value.preset === preset && 'bg-muted font-medium'
						)}
						onClick={() => {
							// Drop any custom endpoints along with the preset — keeping them would make a
							// later switch back to "Custom range" silently reuse an abandoned window.
							onChange({ preset });
							setOpen(false);
						}}
					>
						{RANGE_PRESET_LABELS[preset]}
					</button>
				))}

				<div className='mt-2 border-t border-border pt-2'>
					<p className='px-2 pb-1.5 text-xs text-muted-foreground'>{RANGE_PRESET_LABELS.custom}</p>
					{/*
					 * The shared picker, which only calls back once BOTH ends are chosen — so a
					 * half-made selection never fires a request for a window nobody asked for.
					 */}
					<DateRangePicker
						inline
						className='w-full'
						value={{
							startDate: value.from ? new Date(value.from) : undefined,
							endDate: value.to ? new Date(value.to) : undefined
						}}
						onChange={(range) => {
							if (!range.startDate || !range.endDate) return;
							onChange({
								preset: 'custom',
								from: range.startDate.toISOString(),
								to: range.endDate.toISOString()
							});
							setOpen(false);
						}}
					/>
				</div>
			</PopoverContent>
		</Popover>
	);
};
